// JSON-LD helpers. Build objects in code; serialize once with safeJsonLd().

export const SITE_URL = "https://bizaclia.com";
export const SITE_ID = `${SITE_URL}#id`;
export const WEBSITE_ID = `${SITE_URL}#website`;

export type JsonLdNode = Record<string, unknown>;

function pruneEmpty<T>(value: T): T {
  if (Array.isArray(value)) {
    const cleaned = (value as unknown[])
      .map((v) => pruneEmpty(v))
      .filter((v) => v !== undefined);
    return cleaned as unknown as T;
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const cleaned = pruneEmpty(v);
      if (cleaned === undefined || cleaned === null || cleaned === "") continue;
      if (Array.isArray(cleaned) && cleaned.length === 0) continue;
      if (
        typeof cleaned === "object" &&
        !Array.isArray(cleaned) &&
        Object.keys(cleaned as object).length === 0
      )
        continue;
      out[k] = cleaned;
    }
    return out as unknown as T;
  }
  return value;
}

/** Strip HTML/markdown to text; collapse whitespace. */
export function plainText(input: string | null | undefined): string {
  if (!input) return "";
  return input
    .replace(/<[^>]+>/g, " ")
    .replace(/[*_`~#>]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Serialize JSON-LD and neutralize `</...>` sequences. */
export function safeJsonLd(data: unknown): string {
  return JSON.stringify(pruneEmpty(data)).replace(/<\//g, "<\\/");
}

/** Identity node — BIZACLIA SL acts as a real estate agency with a physical office. */
export function identityNode(): JsonLdNode {
  return {
    "@type": "RealEstateAgent",
    "@id": SITE_ID,
    name: "Bizaclia",
    legalName: "BIZACLIA SL",
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.ico`,
    image: `${SITE_URL}/favicon.ico`,
    email: "administration@esfericgroup.com",
    vatID: "B88822986",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Avenida Más Fuster 27",
      addressLocality: "Sant Cugat del Vallès",
      addressRegion: "Barcelona",
      postalCode: "08197",
      addressCountry: "ES",
    },
    areaServed: { "@type": "Country", name: "España" },
    sameAs: [
      "https://www.instagram.com/bizaclia/",
      "https://www.tiktok.com/@bizaclia",
      "https://www.linkedin.com/company/bizaclia",
    ],
  };
}

export function websiteNode(lang: string = "es"): JsonLdNode {
  return {
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: SITE_URL,
    name: "Bizaclia",
    inLanguage: lang,
    publisher: { "@id": SITE_ID },
  };
}
